import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; 
import API from '../services/api';

const ElectionPortal = () => {
    const [poll, setPoll] = useState(null);
    // 🌟 selectedVotes holds one vote per position: { "President": 5, "Secretary": 12 }
    const [selectedVotes, setSelectedVotes] = useState({}); 
    const [user, setUser] = useState(null);
    const [hasVoted, setHasVoted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false); // 🌟 Tracks the submission state
    const navigate = useNavigate();

    // 🌟 LOCAL URL
    const LOCAL_API_URL = 'https://sherianalumni.com/api/';

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
            navigate('/login');
            return;
        }
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        fetchPollData(parsedUser.id);
    }, [navigate]);

   const fetchPollData = async (userId) => {
        try {
            const res = await axios.get(`${LOCAL_API_URL}/get_active_poll.php?user_id=${userId}`);
            
            // 🌟 UPDATED: Check the visibility flag first
            // If the admin turned it off, we set poll to null, which hides the UI
            if (res.data && res.data.election_visible === false) {
                setPoll(null);
            } 
            // If visible and a poll exists, set it
            else if (res.data && res.data.poll) {
                setPoll(res.data.poll);
                setHasVoted(res.data.hasVoted);
            } 
            // If visible but no poll exists
            else {
                setPoll(null);
            }
            
            setLoading(false);
        } catch (err) {
            console.error("Connection Error:", err);
            setLoading(false);
        }
    };

    // Handle selecting a candidate for a specific position
    const handleSelect = (positionName, optionId) => {
        if (!hasVoted && !submitting) {
            setSelectedVotes(prev => ({
                ...prev,
                [positionName]: optionId
            }));
        }
    };

    const handleVote = async () => {
        // 🌟 VALIDATION: Ensure all positions are filled
        const requiredCount = Object.keys(groupedPositions).length;
        const votedCount = Object.keys(selectedVotes).length;
        
        if (votedCount < requiredCount) {
            return alert(`Please select a candidate for all ${requiredCount} positions before submitting.`);
        }
        
        const confirmVote = window.confirm("Are you sure? This is an official decision and cannot be changed later.");
        if (!confirmVote) return;

        setSubmitting(true); // 🌟 Disable button immediately on click

        try {
            // 🌟 SUBMITS ALL VOTES AT ONCE
            const res = await axios.post(`${LOCAL_API_URL}/submit_vote.php`, {
                user_id: user.id, 
                poll_id: poll.id, 
                votes: selectedVotes
            });
            
            // 🌟 SMART FIX: Improved response check to prevent "undefined" alerts
            if (res.data && res.data.success) {
                setHasVoted(true);
                fetchPollData(user.id); 
                alert("Success: " + (res.data.message || "Your official ballot has been submitted successfully!"));
            } else {
                // Shows the specific server error message
                alert("Server Error: " + (res.data.message || "The server could not record your vote. check database columns."));
            }
        } catch (err) { 
            // 🌟 SMART FIX: Improved catch block to log the real reason for failure
            console.error("Full Error Object:", err);
            const errorMsg = err.response?.data?.message || "Connection failed. Please check your XAMPP server.";
            alert("Voting failed: " + errorMsg); 
        } finally {
            setSubmitting(false);
        }
    };

    // Group the options by their Position Name
    const groupedPositions = {};
    if (poll && poll.options) {
        poll.options.forEach(opt => {
            const pos = opt.position_name || 'General';
            if (!groupedPositions[pos]) groupedPositions[pos] = [];
            groupedPositions[pos].push(opt);
        });
    }

    // 🌟 LOGIC: Determine if the ballot is full (ready to submit)
    const isBallotComplete = poll && Object.keys(groupedPositions).length === Object.keys(selectedVotes).length;

    return (
        <div className="election-wrapper">
            <style>{`
                .election-wrapper { 
                    min-height: 100vh; 
                    padding-top: 120px; 
                    background: #134628; /* 🌟 UPDATED TO PREMIUM GREEN */
                    font-family: 'Segoe UI', sans-serif; 
                    padding-bottom: 80px; 
                }
                .election-header { text-align: center; max-width: 800px; margin: 0 auto 40px; z-index: 10; position: relative; }
                .election-header h1 { font-size: 3.5rem; font-weight: 900; color: white; text-transform: uppercase; font-style: italic; letter-spacing: -2px; }
                .election-header h1 span { color: #ffc300; }
                
                .election-warning { 
                    background: rgba(255, 195, 0, 0.1); 
                    border: 1px solid #ffc300; 
                    color: #ffc300; 
                    padding: 15px; 
                    border-radius: 15px; 
                    margin-bottom: 30px; 
                    display: inline-block; 
                    font-weight: 700;
                    font-size: 0.9rem;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .poll-card { background: rgba(255,255,255,0.05); border-radius: 40px; padding: 60px; color: white; max-width: 1000px; margin: 0 auto; box-shadow: 0 40px 80px rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); backdrop-filter: blur(10px); }
                
                .position-section { margin-top: 40px; text-align: left; }
                .position-title { color: #ffc300; font-weight: 800; border-bottom: 2px solid rgba(255, 195, 0, 0.3); padding-bottom: 10px; margin-bottom: 20px; text-transform: uppercase; letter-spacing: 1px; }

                .entity-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; }
                
                .entity-option { 
                    background: rgba(255,255,255,0.03); 
                    border: 2px solid rgba(255,255,255,0.1); 
                    border-radius: 25px; 
                    padding: 35px; 
                    text-align: center; 
                    cursor: pointer; 
                    transition: 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); 
                }
                
                .entity-option:hover { border-color: rgba(255, 195, 0, 0.5); transform: translateY(-5px); background: rgba(255,255,255,0.06); }
                .entity-option.selected { 
                    background: rgba(255, 195, 0, 0.15); 
                    border-color: #ffc300; 
                    box-shadow: 0 0 30px rgba(255, 195, 0, 0.2);
                    transform: scale(1.02);
                }

                .results-bar { height: 8px; background: rgba(255,255,255,0.1); border-radius: 10px; margin-top: 10px; overflow: hidden; }
                .results-fill { height: 100%; background: #ffc300; transition: 1s; }
                
                .btn-vote { 
                    background: #ffc300; 
                    color: #134628; /* 🌟 UPDATED TO PREMIUM GREEN */
                    border: none; 
                    padding: 20px 60px; 
                    font-size: 1.2rem; 
                    font-weight: 900; 
                    border-radius: 100px; 
                    margin-top: 40px; 
                    cursor: pointer; 
                    text-transform: uppercase; 
                    transition: 0.4s; 
                    box-shadow: 0 15px 35px rgba(255, 195, 0, 0.4);
                }
                .btn-vote:hover:not(:disabled) { transform: scale(1.05); background: white; }
                .btn-vote:disabled { background: #4a5568; color: #a0aec0; cursor: not-allowed; box-shadow: none; transform: none; opacity: 0.6; }

                .no-poll-box { padding: 80px 40px; text-align: center; background: rgba(255,255,255,0.03); border-radius: 40px; border: 1px dashed rgba(255,255,255,0.2); }

                @media (max-width: 768px) {
                    .election-wrapper { padding-top: 100px; padding-bottom: 40px; }
                    .election-header { margin-bottom: 25px; }
                    .election-header h1 { font-size: 2.1rem !important; letter-spacing: -1px; line-height: 1.1; }
                    .poll-card { padding: 30px 20px !important; border-radius: 25px; }
                    .election-warning { font-size: 0.75rem; padding: 10px; margin-bottom: 20px; width: 100%; }
                    .display-6 { font-size: 1.5rem !important; }
                    .fs-5 { font-size: 1rem !important; }
                    .entity-grid { grid-template-columns: 1fr; gap: 15px; margin-top: 20px; }
                    .entity-option { padding: 20px !important; border-radius: 15px; }
                    .entity-option h4 { font-size: 1.1rem; }
                    .btn-vote { width: 100%; padding: 16px 20px; font-size: 1rem; margin-top: 25px; }
                }
            `}</style>

            <div className="container">
                <div className="election-header">
                    <h1>SHERIAN <span>DECISION PORTAL</span></h1>
                </div>

                {loading ? (
                    <div className="text-center p-5"><i className="fas fa-circle-notch fa-spin fa-3x text-warning"></i></div>
                ) : !poll ? (
                    <div className="no-poll-box">
                        <i className="fas fa-hourglass-half fa-4x mb-4 text-warning opacity-50"></i>
                        <h2 className="text-white fw-bold">No Active Decisions</h2>
                        <p className="text-white opacity-50 fs-5">Please check back later for upcoming elections.</p>
                    </div>
                ) : (
                    <div className="poll-card text-center">
                        
                        {!hasVoted && (
                            <div className="election-warning">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {isBallotComplete ? "Your ballot is complete. You may now submit your vote." : "Notice: You must select one candidate for every position to activate the submit button."}
                            </div>
                        )}

                        <div className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill fw-bold">OFFICIAL ELECTION</div>
                        <h2 className="display-6 fw-bold mb-2">{poll.title}</h2>
                        <p className="opacity-75 fs-5 mb-4">{poll.description}</p>
                        
                        {/* Loops through the positions and creates a grid for each one */}
                        {Object.keys(groupedPositions).map(posName => (
                            <div key={posName} className="position-section">
                                <h3 className="position-title">{posName}</h3>
                                
                                <div className="entity-grid">
                                    {groupedPositions[posName].map(opt => (
                                        <div 
                                            key={opt.id} 
                                            className={`entity-option ${selectedVotes[posName] === opt.id ? 'selected' : ''}`} 
                                            onClick={() => handleSelect(posName, opt.id)}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h4 className="fw-bold m-0">{opt.option_name}</h4>
                                                {!hasVoted && selectedVotes[posName] === opt.id && (
                                                    <i className="fas fa-check-circle text-warning fs-4"></i>
                                                )}
                                            </div>
                                            
                                            {hasVoted && (
                                                <>
                                                    <div className="d-flex justify-content-between small mt-3 font-monospace"><span>VOTES</span><span>{opt.percentage}%</span></div>
                                                    <div className="results-bar"><div className="results-fill" style={{width: `${opt.percentage}%`}}></div></div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div className="text-center">
                            {hasVoted ? (
                                <div className="mt-5 p-3 bg-success bg-opacity-25 rounded-4 border border-success border-opacity-25 text-success fw-bold">
                                    <i className="fas fa-check-circle me-2"></i> YOUR OFFICIAL BALLOT HAS BEEN RECORDED
                                </div>
                            ) : (
                                <button 
                                    className="btn-vote" 
                                    onClick={handleVote}
                                    /* Inactive until all positions selected OR during submission */
                                    disabled={!isBallotComplete || submitting}
                                >
                                    {submitting ? (
                                        <><i className="fas fa-spinner fa-spin me-2"></i> Recording Vote...</>
                                    ) : (
                                        <>Cast Official Ballot <i className="fas fa-paper-plane ms-2"></i></>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ElectionPortal;